<template>
  <div class="flex items-center gap-2">
    <Button variant="outline" size="sm" type="button" class="h-7 max-w-60 justify-start gap-1.5 rounded-full border-transparent bg-background px-2.5 pr-3 text-xs font-medium text-muted-foreground shadow-none ring-1 ring-border hover:bg-transparent" :loading="isUpdating" @click="openAssigneeModal">
      <span class="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-primary text-[10px] font-semibold uppercase text-primary-foreground">
        {{ assigneeInitials }}
      </span>
      <span v-if="showLabel" class="text-xs text-muted-foreground">
        {{ task.assigned_to_user_id ? 'Assignee' : 'Not assigned' }}
      </span>
      <span class="truncate">{{ assigneeLabel }}</span>
    </Button>
    <TaskAssignToUserModal ref="assignToUserModal" :task-id="taskId" :assignee-id="normalizedAssigneeId" :context="context" @loaded-users="setAssignableUsers" />
  </div>
</template>

<script setup>
import { computed, ref } from 'vue'
import { Button } from '../../../../../../../../resources/js/components/ui/button/index.js'
import { useTasksStore } from '../../../stores/tasksStore.js'
import TaskAssignToUserModal from './TaskAssignToUserModal.vue'

const assignToUserModal = ref(null)
const assignableUsers = ref([])

const props = defineProps({
  task: {
    type: Object,
    required: true,
  },
  value: {
    type: [Number, String, Object],
    default: null,
  },
  context: {
    type: String,
    default: 'default',
  },
  showLabel: {
    type: Boolean,
    default: false,
  },
})

const tasksStore = useTasksStore(props.context)
const taskId = computed(() => Number(props.task?.id))
const normalizedAssigneeId = computed(() => {
  const parsedId = Number(props.value)

  if (!Number.isInteger(parsedId) || parsedId <= 0) {
    return null
  }

  return parsedId
})
const assigneeUser = computed(() => assignableUsers.value.find((user) => user.id === normalizedAssigneeId.value) ?? null)
const cachedAssigneeName = computed(() => {
  const name = props.task?.cached?.assigned_to_user?.name

  if (typeof name !== 'string' || name.trim() === '') {
    return null
  }

  return name
})
const assigneeLabel = computed(() => {
  if (assigneeUser.value) {
    return assigneeUser.value.name
  }

  if (cachedAssigneeName.value) {
    return cachedAssigneeName.value
  }

  if (normalizedAssigneeId.value !== null) {
    return `User #${normalizedAssigneeId.value}`
  }

  return 'Unassigned'
})
const assigneeInitials = computed(() => {
  if (normalizedAssigneeId.value === null) {
    return '--'
  }

  const initials = assigneeLabel.value
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part.charAt(0))
    .join('')
    .toUpperCase()

  return initials !== '' ? initials : String(normalizedAssigneeId.value).slice(0, 2)
})
const isUpdating = computed(() => tasksStore.isUpdatingAssignee(taskId.value))

const openAssigneeModal = () => {
  assignToUserModal.value?.openModal?.()
}

const setAssignableUsers = (users) => {
  assignableUsers.value = Array.isArray(users) ? users : []
}
</script>
